import Airtable from 'airtable';
import axios from 'axios';
import Fuse from 'fuse.js';

const base = new Airtable({
  apiKey: 'pattCKvlVPibAMZw1.6eb525b7643d3b8c59cfed53e7dd58128f181dca3f4a155ce614303fa1a29329'
}).base('appo8g9TjuRyPjwA9');

export const employersTable = base('Companies');

export const savedScopesTable = base('Saved Scopes');

export const organizationsTable = base('Organization');

export const programsTable = base('Program');

const participantsTable = base('Student');

export interface Employer {
  id: string;
  name: string;
  industry: string;
  website: string;
  shortDescription: string;
  employeeCount: string;
  schoolDistrict: string;
  pathways: string[];
  moreNotes: string;
  rank: number;
  jurisdiction: string;
}

export interface SavedScope {
  id?: string;
  userId: string;
  email?: string;
  deviceInfo?: {
    id: string;
    browser: string;
    platform: string;
    language: string;
    timeZone: string;
    firstSeen: string;
  };
  savedAt: string;
  savedInputs: Record<string, string>;
  scopesList: Array<any>;
  scopesPerEmployer: number;
  selectedScopes: Array<any>;
  name?: string;
  roadmap?: {
    phases: Array<{
      title: string;
      description: string;
      activities: string[];
      duration?: string;
      outcomes?: string[];
    }>;
    summary: string;
    estimatedDuration: string;
    keySkills: string[];
    success: boolean;
    rawResponse?: string;
    parsedSuccessfully?: boolean;
    generatedAt: string;
  };
}

export interface Organization {
  id: string;
  title: string;
  domain: string;
  domains: string[];
  organizationJoinLink: string;
  users: number;
  participants: number;
  programs: number;
  _id: string | null;
  members: number;
}

export interface Program {
  id: string;
  programName: string;
  programDesc: string;
  domains: string;
  participants: string;
}

export interface Participant {
  id: string;
  fullName: string;
  programName: string;
  programDomains: string;
  programDesc?: string;
  schoolDistrict: string;
  domainOnly?: string;
}

/**
 * Fetch employers via Airtable REST API (using raw Axios calls).
 * Switches pagination to use Airtable's opaque cursor string (offset).
 */
export async function fetchEmployers(
  options: {
    searchQuery?: string;
    pageSize?: number;
    offset?: string | null;
    includeTotal?: boolean;
    sort?: { field: string; direction?: 'asc' | 'desc' }[];
  } = {}
): Promise<{ records: Employer[]; hasMore: boolean; nextOffset?: string | null; total?: number }> {
  const {
    searchQuery = '',
    pageSize = 50,
    offset = null,
    includeTotal = false,
    sort = []
  } = options;

  // Build the Airtable formula for search (same logic as before)
  let filterFormula = '';
  if (searchQuery) {
    const searchTerms = searchQuery
      .toLowerCase()
      .split(' ')
      .filter(term => term.length > 0);

    const mainFilter = searchTerms.map(
      term => `OR(
        SEARCH('${term}', LOWER({Company Name})),
        SEARCH('${term}', LOWER({Industry})),
        SEARCH('${term}', LOWER({Website})),
        SEARCH('${term}', LOWER({Short Description})),
        SEARCH('${term}', LOWER({School District})),
        SEARCH('${term}', LOWER({Pathways})),
        SEARCH('${term}', LOWER({More Notes})),
        SEARCH('${term}', LOWER({Jurisdiction}))
      )`
    );

    filterFormula = `AND(${mainFilter.join(',')})`;
  }

  try {
    // Query parameters for the REST call
    const params: Record<string, string | number> = {
      view: 'Official',
      pageSize: String(pageSize)
    };

    if (offset) {
      params.offset = offset;
    }

    if (filterFormula) {
      params.filterByFormula = filterFormula;
    }

    // Sort parameters: sort[0][field], sort[0][direction], …
    sort.forEach((s, idx) => {
      params[`sort[${idx}][field]`] = s.field;
      if (s.direction) {
        params[`sort[${idx}][direction]`] = s.direction;
      }
    });

    const AIRTABLE_API_KEY =
      'pattCKvlVPibAMZw1.6eb525b7643d3b8c59cfed53e7dd58128f181dca3f4a155ce614303fa1a29329';
    const BASE_ID = 'appo8g9TjuRyPjwA9';
    const TABLE_NAME = 'Companies';

    const { data } = await axios.get<{ records: any[]; offset?: string }>(
      `https://api.airtable.com/v0/${BASE_ID}/${encodeURIComponent(TABLE_NAME)}`,
      {
        params,
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const rawRecords = data.records || [];

    // Map REST records into Employer objects
    const mappedRecords = rawRecords.map(rec => {
      const fields = rec.fields || {};
      return {
        id: rec.id,
        name: fields['Company Name'] || '',
        industry: fields.Industry || '',
        website: fields.Website || '',
        shortDescription: fields['Short Description'] || '',
        jurisdiction: fields.Jurisdiction || '',
        pathways: fields.Pathways || [],
        employeeCount: fields['Employee Count'] || '',
        schoolDistrict: fields['School District'] || '',
        moreNotes: fields['More Notes'] || '',
        rank: fields.Rank || 0
      } as Employer;
    });

    // Relevance ranking with Fuse.js when a search query exists
    let finalRecords: Employer[] = mappedRecords;
    if (searchQuery.trim().length > 0) {
      const fuse = new Fuse(mappedRecords, {
        shouldSort: true,
        threshold: 0.4, // lower is stricter; 0.4 good balance for names
        keys: [
          { name: 'name', weight: 0.6 },
          { name: 'industry', weight: 0.15 },
          { name: 'website', weight: 0.05 },
          { name: 'shortDescription', weight: 0.1 },
          { name: 'jurisdiction', weight: 0.05 },
          { name: 'pathways', weight: 0.05 }
        ]
      });

      finalRecords = fuse.search(searchQuery).map(r => r.item);
    }

    const hasMore = typeof data.offset === 'string';

    // NOTE: includeTotal is expensive with REST (no count endpoint). We skip for now.
    return {
      records: finalRecords,
      hasMore,
      nextOffset: data.offset ?? null,
      total: undefined
    };
  } catch (error) {
    console.error('Error fetching employers via REST:', error);
    throw error;
  }
}

export async function saveScopesToAirtable(data: SavedScope) {
  try {
    const fields = {
      'User ID': data.userId,
      Email: data.email,
      'Device Info': data.deviceInfo ? JSON.stringify(data.deviceInfo) : '',
      'Saved At': data.savedAt,
      'Saved Inputs': JSON.stringify(data.savedInputs),
      'Scopes List': JSON.stringify(data.scopesList),
      'Scopes Per Employer': data.scopesPerEmployer,
      'Selected Scopes': JSON.stringify(data.selectedScopes),
      Name: data.name || `Save from ${new Date(data.savedAt).toLocaleDateString()}`,
      Roadmap: data.roadmap ? JSON.stringify(data.roadmap) : ''
    };

    const record = await savedScopesTable.create([{ fields }]);
    return record[0];
  } catch (error) {
    console.error('Error saving to Airtable:', error);
    throw error;
  }
}

export async function loadScopesFromAirtable(userId: string): Promise<SavedScope[]> {
  try {
    const records = await savedScopesTable
      .select({
        filterByFormula: `{User ID} = '${userId}'`,
        sort: [{ field: 'Saved At', direction: 'desc' }]
      })
      .all();

    return records.map(record => ({
      id: record.id,
      userId: (record.get('User ID') as string) || '',
      savedAt: (record.get('Saved At') as string) || '',
      savedInputs: record.get('Saved Inputs')
        ? JSON.parse(record.get('Saved Inputs') as string)
        : {},
      scopesList: record.get('Scopes List') ? JSON.parse(record.get('Scopes List') as string) : [],
      scopesPerEmployer: (record.get('Scopes Per Employer') as number) || 1,
      selectedScopes: record.get('Selected Scopes')
        ? JSON.parse(record.get('Selected Scopes') as string)
        : [],
      name: (record.get('Name') as string) || '',
      email: (record.get('Email') as string) || '',
      deviceInfo: record.get('Device Info')
        ? JSON.parse(record.get('Device Info') as string)
        : null,
      roadmap: record.get('Roadmap') ? JSON.parse(record.get('Roadmap') as string) : null
    }));
  } catch (error) {
    console.error('Error loading from Airtable:', error);
    throw error;
  }
}

export async function migrateDeviceSaves(deviceId: string, userId: string) {
  try {
    const deviceSaves = await loadScopesFromAirtable(deviceId);

    // Use Promise.all with map instead of for...of loop
    await Promise.all(
      deviceSaves.map(save =>
        saveScopesToAirtable({
          ...save,
          userId
        })
      )
    );

    return true;
  } catch (error) {
    console.error('Error migrating device saves:', error);
    throw error;
  }
}

export async function fetchOrganizations(
  options: {
    searchQuery?: string;
    searchField?: string;
    pageSize?: number;
    offset?: string;
  } = {}
): Promise<Organization[]> {
  const { searchQuery = '', searchField = 'title', pageSize = 100, offset = null } = options;

  let filterFormula = '';
  if (searchQuery) {
    if (searchField === 'domain') {
      filterFormula = `OR(${searchQuery
        .split(' ')
        .map(term => `SEARCH('${term}', LOWER({Domain}))`)
        .join(', ')})`;
    } else {
      filterFormula = `OR(${searchQuery
        .split(' ')
        .map(term => `SEARCH('${term}', LOWER({Title}))`)
        .join(', ')})`;
    }
  }

  try {
    const selectOptions: any = {
      view: 'Official',
      maxRecords: 1000,
      filterByFormula: filterFormula
    };

    if (typeof offset === 'number') {
      selectOptions.offset = offset;
    }

    const records = await organizationsTable.select(selectOptions).all();

    return records.map(record => ({
      id: record.id,
      title: record.get('Title') as string,
      domain: record.get('Domain') as string,
      domains: (record.get('Domains') as string[]) || [],
      organizationJoinLink: record.get('Organization Join Link') as string,
      users: (record.get('Users') as number) || 0,
      participants: (record.get('Participants') as number) || 0,
      programs: (record.get('Programs') as number) || 0,
      _id: (record.get('_id') as string) || null,
      members: (record.get('Members') as number) || 0
    }));
  } catch (error) {
    console.error('Error fetching organizations:', error);
    throw error;
  }
}

export async function fetchPrograms(): Promise<Program[]> {
  try {
    const records = await programsTable
      .select({
        view: 'Official'
      })
      .all();

    return records.map(record => ({
      id: record.id,
      programName: record.get('programName') as string,
      programDesc: record.get('programDesc') as string,
      domains: record.get('Domains (Formula)') as string,
      participants: record.get('Count (Student) - All Years') as string
    }));
  } catch (error) {
    console.error('Error fetching programs:', error);
    throw error;
  }
}

export async function fetchParticipants(): Promise<Participant[]> {
  try {
    const records = await participantsTable
      .select({
        view: 'Official'
      })
      .all();

    return records.map(record => ({
      id: record.id,
      fullName: record.get('ADMIN - Full Name') as string,
      programName: record.get('programName') as string,
      programDomains: ((record.get('Domains (Formula) (from Program)') as string[]) || []).join(
        ', '
      ),
      programDesc: ((record.get('programDesc (from Program)') as string[]) || []).join(', '),
      schoolDistrict: (
        (record.get('portfolio.school.district (from StudentPortfolio)') as string[]) || []
      ).join(', '),
      domainOnly: record.get('Domain Only (Stakeholders (Link to User))') as string,
      tderCount: Array.isArray(
        record.get('Count (TDER (Link to Student)) (from TDER (Link to User))')
      )
        ? record.get('Count (TDER (Link to Student)) (from TDER (Link to User))')[0] || 0
        : (record.get('Count (TDER (Link to Student)) (from TDER (Link to User))') as number),
      gender: record.get('Gender') as string,
      language: record.get('portfolio.home.language (from StudentPortfolio)') as string,
      grade: record.get('portfolio.grade (from StudentPortfolio)') as string,
      pathways: record.get('pathways (from adk.start) (from Program)') as string,
      school: record.get('portfolio.school.name (from StudentPortfolio)') as string,
      zipcode: record.get('portfolio.home.zipcode (from StudentPortfolio)') as string,
      city: record.get('portfolio.home.city (from StudentPortfolio)') as string,
      ethnicity: record.get('portfolio.ethnicity (from StudentPortfolio)') as string,
      birthYear: record.get('birthDate - YEAR') as string,
      percentageCompleted: record.get('percentageCompleted') as string
    }));
  } catch (error) {
    console.error('Error fetching participants:', error);
    return [];
  }
}

export async function fetchParticipantsByDomain(domain: string): Promise<Participant[]> {
  try {
    const filterFormula = `SEARCH('${domain}', {Domains (Formula) (from Program)})`;

    const records = await participantsTable
      .select({
        view: 'Official'
        // filterByFormula: filterFormula
      })
      .all();

    return records.map(record => ({
      id: record.id,
      fullName: record.get('ADMIN - Full Name') as string,
      programName: record.get('programName') as string,
      programDomains: record.get('Domains (Formula) (from Program)') as string,
      schoolDistrict: record.get('portfolio.school.district (from StudentPortfolio)') as string
    }));
  } catch (error) {
    console.error('Error fetching participants by domain:', error);
    throw error;
  }
}

export async function fetchOptionsByType(type: string) {
  const records = await base('Qualification')
    .select({
      view: 'Official',
      filterByFormula: `{Type} = "${type}"`
    })
    .all();

  return records.map(record => record.fields.Name); // Assuming 'Name' is the field with the options
}

export async function fetchParticipantsByFilters(filters: {
  domain?: string;
  schoolDistrict?: string;
  pathway?: string;
  industries?: string[];
  city?: string;
  zipcode?: string;
  grade?: string;
  gender?: string;
  language?: string[];
  ethnicity?: string[];
  birthYear?: string;
  percentageCompleted?: string;
  type?: string;
  organization?: string;
}): Promise<Participant[]> {
  try {
    const filterFormula = [];

    if (filters.schoolDistrict) {
      filterFormula.push(`{School District} = '${filters.schoolDistrict}'`);
    }
    if (filters.pathway) {
      filterFormula.push(`SEARCH('${filters.pathway}', {Pathways})`);
    }
    if (filters.industries && filters.industries.length > 0) {
      const industriesFilter = filters.industries
        .map(industry => `SEARCH('${industry}', {Industry})`)
        .join(', ');
      filterFormula.push(`OR(${industriesFilter})`);
    }
    if (filters.city) {
      filterFormula.push(`{City} = '${filters.city}'`);
    }
    if (filters.zipcode) {
      filterFormula.push(`{Zipcode} = '${filters.zipcode}'`);
    }
    if (filters.grade) {
      filterFormula.push(`{Grade} = '${filters.grade}'`);
    }
    if (filters.gender) {
      filterFormula.push(`{Gender} = '${filters.gender}'`);
    }
    if (filters.language && filters.language.length > 0) {
      const languageFilter = filters.language
        .map(lang => `SEARCH('${lang}', {Language})`)
        .join(', ');
      filterFormula.push(`OR(${languageFilter})`);
    }
    if (filters.ethnicity && filters.ethnicity.length > 0) {
      const ethnicityFilter = filters.ethnicity
        .map(eth => `SEARCH('${eth}', {Ethnicity})`)
        .join(', ');
      filterFormula.push(`OR(${ethnicityFilter})`);
    }
    if (filters.birthYear) {
      filterFormula.push(`{Birth Year} = '${filters.birthYear}'`);
    }
    if (filters.percentageCompleted) {
      filterFormula.push(`{percentageCompleted} = '${filters.percentageCompleted}'`);
    }
    if (filters.type) {
      filterFormula.push(`{Type} = '${filters.type}'`);
    }
    if (filters.organization) {
      filterFormula.push(`{Organization} = '${filters.organization}'`);
    }

    const formula = filterFormula.length > 0 ? `AND(${filterFormula.join(', ')})` : '';

    const records = await participantsTable
      .select({
        view: 'Official',
        filterByFormula: formula
      })
      .all();

    // Filter records by domain in JavaScript if domain filter is specified
    const filteredRecords = filters.domain
      ? records.filter(record => {
          const domainField = record.get('Domains (Formula) (from Program)') as string[];
          const domains = Array.isArray(domainField) ? domainField : [];
          return domains.includes(filters.domain);
        })
      : records;

    return filteredRecords.map(record => ({
      id: record.id,
      fullName: record.get('ADMIN - Full Name') as string,
      programName: record.get('programName') as string,
      programDesc: record.get('programDesc (from Program)') as string,
      programDomains: record.get('Domains (Formula) (from Program)') as string,
      schoolDistrict: record.get('portfolio.school.district (from StudentPortfolio)') as string,
      gender: record.get('portfolio.gender (from StudentPortfolio)') as string,
      language: record.get('portfolio.home.language (from StudentPortfolio)') as string,
      grade: record.get('portfolio.grade (from StudentPortfolio)') as string,
      pathways: record.get('pathways (from adk.start) (from Program)') as string,
      school: record.get('portfolio.school.name (from StudentPortfolio)') as string,
      zipcode: record.get('portfolio.home.zipcode (from StudentPortfolio)') as string,
      city: record.get('portfolio.home.city (from StudentPortfolio)') as string,
      ethnicity: record.get('portfolio.ethnicity (from StudentPortfolio)') as string,
      birthYear: record.get('birthDate - YEAR') as string,
      percentageCompleted: record.get('Percentage Completed') as string
    }));
  } catch (error) {
    console.error('Error fetching participants with filters:', error);
    throw error;
  }
}
